import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '@/_components/ui'
import { mutations } from '@/features/_lib/react-query/mutations'

type ReleaseHandoffButtonProps = {
  applicationId: string
  companyId: string
  facilityId: string
  size?: 'default' | 'xs'
}

// Releases a parked application and triggers the deferred approval messages.
export const ReleaseHandoffButton = ({
  applicationId,
  companyId,
  facilityId,
  size = 'default',
}: ReleaseHandoffButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const queryClient = useQueryClient()
  const releaseMutation = useMutation(mutations.applications.releaseHandoff(queryClient))

  const handleRelease = () => {
    releaseMutation.mutate(
      { applicationId, companyId, facilityId },
      {
        onSuccess: () => {
          toast.success('Proposta liberada e enviada ao paciente')
          setIsDialogOpen(false)
        },
        onError: () => {
          toast.error('Erro ao liberar a proposta')
        },
      },
    )
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button size={size} className="whitespace-nowrap font-bold">
          Liberar proposta
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[440px]">
        <DialogTitle>Liberar envio da proposta</DialogTitle>
        <p className="text-sm text-brand-grayscale-8">
          O paciente receberá a mensagem de aprovação com os detalhes da proposta pelo WhatsApp.
          Deseja continuar?
        </p>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="gray" className="font-bold" disabled={releaseMutation.isPending}>
              Cancelar
            </Button>
          </DialogClose>
          <Button disabled={releaseMutation.isPending} onClick={handleRelease}>
            {releaseMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Liberar e enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
